import React from 'react'

// Простая версия для тестирования
export const simpleScrollTo = (targetId: string, offset: number = 0) => {
  const targetSection = document.getElementById(targetId)
  if (!targetSection) {
    console.log('simpleScrollTo: target not found:', targetId)
    return
  }
  
  const targetPosition = targetSection.offsetTop - offset
  console.log('simpleScrollTo:', { targetId, offset, targetPosition, originalTop: targetSection.offsetTop })
  
  // Используем window.scrollTo вместо scrollIntoView для правильного учета offset
  window.scrollTo({
    top: targetPosition,
    behavior: 'smooth'
  })
}

// Функция для скролла до конца страницы
export const scrollToBottom = () => {
  const scrollHeight = document.documentElement.scrollHeight
  const clientHeight = document.documentElement.clientHeight
  const maxScroll = scrollHeight - clientHeight
  
  console.log('scrollToBottom:', { scrollHeight, clientHeight, maxScroll })
  
  // Скроллим не до самого конца, а немного выше
  const targetPosition = maxScroll - 100 // Оставляем 100px от конца
  
  const startPosition = window.pageYOffset
  const distance = targetPosition - startPosition
  
  // Плавная анимация
  const duration = 2000
  let start: number | null = null

  const animation = (currentTime: number) => {
    if (start === null) start = currentTime
    const timeElapsed = currentTime - start
    const progress = Math.min(timeElapsed / duration, 1)
    
    // Используем мягкую функцию easing
    const easeProgress = easeInOutCubic(progress)
    const currentPosition = startPosition + (distance * easeProgress)
    
    window.scrollTo(0, currentPosition)
    
    if (progress < 1) {
      requestAnimationFrame(animation)
    } else {
      // Принудительно устанавливаем финальную позицию
      window.scrollTo(0, targetPosition)
      console.log('scrollToBottom completed:', { targetPosition, finalPosition: window.pageYOffset })
    }
  }

  // Мягкая функция easing - кубическая
  const easeInOutCubic = (t: number): number => {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
  }

  requestAnimationFrame(animation)
}

// Улучшенная версия с более мягкой анимацией
export const smoothScrollTo = (
  targetId: string, 
  offset: number = 0,
  onComplete?: () => void
): (() => void) => {
  const targetSection = document.getElementById(targetId)
  if (!targetSection) {
    console.log('smoothScrollTo: target not found:', targetId)
    return () => {}
  }

  const startPosition = window.pageYOffset
  const targetPosition = targetSection.offsetTop - offset
  const distance = targetPosition - startPosition
  
  console.log('smoothScrollTo start:', { targetId, offset, targetPosition, startPosition, distance, originalTop: targetSection.offsetTop })
  
  // Если расстояние очень маленькое, скроллим мгновенно
  if (Math.abs(distance) < 30) {
    window.scrollTo(0, targetPosition)
    onComplete?.()
    return () => {}
  }
  
  // Очень плавная и мягкая анимация
  const baseDuration = 1800 // увеличиваем базовую длительность для максимальной плавности
  const distanceFactor = Math.abs(distance) / window.innerHeight
  const duration = Math.min(Math.max(baseDuration * distanceFactor, 1200), 2500)
  
  let start: number | null = null
  let animationId: number | null = null

  const animation = (currentTime: number) => {
    if (start === null) start = currentTime
    const timeElapsed = currentTime - start
    const progress = Math.min(timeElapsed / duration, 1)
    
    // Используем очень мягкую функцию easing
    const easeProgress = easeInOutQuart(progress)
    const currentPosition = startPosition + (distance * easeProgress)
    
    window.scrollTo(0, currentPosition)
    
    // Проверяем, достигли ли мы целевой позиции или близки к ней
    const currentScrollPosition = window.pageYOffset
    const isAtTarget = Math.abs(currentScrollPosition - targetPosition) < 5
    
    if (progress >= 1 || isAtTarget) {
      // Принудительно устанавливаем финальную позицию
      window.scrollTo(0, targetPosition)
      animationId = null
      
      // Дополнительная проверка через небольшую задержку
      setTimeout(() => {
        const finalPosition = window.pageYOffset
        console.log('smoothScrollTo end:', { targetPosition, finalPosition, difference: Math.abs(finalPosition - targetPosition) })
        if (Math.abs(finalPosition - targetPosition) > 5) {
          window.scrollTo(0, targetPosition)
          console.log('Forced final position set')
        }
        onComplete?.()
      }, 50)
    } else {
      animationId = requestAnimationFrame(animation)
    }
  }

  // Очень мягкая функция easing - четвертой степени
  const easeInOutQuart = (t: number): number => {
    return t < 0.5 ? 8 * t * t * t * t : 1 - Math.pow(-2 * t + 2, 4) / 2
  }

  animationId = requestAnimationFrame(animation)
  
  // Возвращаем функцию для отмены анимации
  const cancelAnimation = () => {
    if (animationId) {
      cancelAnimationFrame(animationId)
      animationId = null
    }
  }
  
  return cancelAnimation
}

// Альтернативная версия с плавной анимацией
export const scrollToWithOffset = (targetId: string, offset: number = 0) => {
  const targetSection = document.getElementById(targetId)
  if (!targetSection) {
    console.log('scrollToWithOffset: target not found:', targetId)
    return
  }
  
  console.log('scrollToWithOffset start:', { targetId, offset, originalTop: targetSection.offsetTop })
  
  const startPosition = window.pageYOffset
  const targetPosition = targetSection.offsetTop - offset
  const distance = targetPosition - startPosition
  
  // Используем плавную анимацию вместо резкого перехода
  const duration = 1500
  let start: number | null = null
  
  const animation = (currentTime: number) => {
    if (start === null) start = currentTime
    const timeElapsed = currentTime - start
    const progress = Math.min(timeElapsed / duration, 1)
    
    // Используем мягкую функцию easing
    const easeProgress = easeInOutCubic(progress)
    const currentPosition = startPosition + (distance * easeProgress)
    
    window.scrollTo(0, currentPosition)
    
    if (progress < 1) {
      requestAnimationFrame(animation)
    } else {
      // Принудительно устанавливаем финальную позицию
      window.scrollTo(0, targetPosition)
      console.log('scrollToWithOffset completed:', { targetPosition, finalPosition: window.pageYOffset })
    }
  }

  // Мягкая функция easing - кубическая
  const easeInOutCubic = (t: number): number => {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
  }

  requestAnimationFrame(animation)
}

// Хук для плавного скролла с защитой от множественных кликов
export const useSmoothScroll = () => {
  const isScrolling = React.useRef(false)
  const cancelRef = React.useRef<(() => void) | null>(null)

  const scrollTo = React.useCallback((
    targetId: string, 
    offset: number = 0,
    onComplete?: () => void
  ) => {
    console.log('useSmoothScroll called:', { targetId, offset })
    
    // Предотвращаем множественные клики
    if (isScrolling.current) {
      console.log('Already scrolling, ignoring click')
      return
    }
    
    isScrolling.current = true
    
    // Отменяем предыдущую анимацию если она есть
    if (cancelRef.current) {
      cancelRef.current()
    }
    
    // Для очень больших offset'ов используем простой скролл
    if (Math.abs(offset) > 1000) {
      console.log('Using simpleScrollTo for large offset:', offset)
      simpleScrollTo(targetId, offset)
      setTimeout(() => {
        isScrolling.current = false
        cancelRef.current = null
        onComplete?.()
      }, 1000)
    } else {
      // Используем smoothScrollTo для обычных offset'ов
      cancelRef.current = smoothScrollTo(targetId, offset, () => {
        isScrolling.current = false
        cancelRef.current = null
        onComplete?.()
      })
    }
  }, [])

  // Очистка при размонтировании
  React.useEffect(() => {
    return () => {
      if (cancelRef.current) {
        cancelRef.current()
      }
    }
  }, [])

  return scrollTo
} 